/**
 * ÌTURA gift chooser — cart drawer + cart page (Prestige, 2026-08-16).
 *
 * Ships as an asset, not an inline script, deliberately: Prestige re-renders the cart drawer by
 * swapping innerHTML, and scripts injected that way never execute. A delegated listener bound once
 * on `document` survives every drawer refresh.
 *
 * Contract with snippets/itura-gift-picker.liquid:
 *   [data-itura-gift-add]      = variant id to add, with [data-itura-gift-tier] = "Tier 1|Tier 2|Hero"
 *   [data-itura-gift-surprise] = full cart-attribute value, e.g. "Surprise me (Tier 2)"
 * Slip markers must stay exact — fulfillment reads them off the order.
 */
(function () {
  if (window.__ituraGiftBound) return;
  window.__ituraGiftBound = true;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function isCartPage() {
    try {
      if (window.themeVariables && window.themeVariables.settings) {
        return window.themeVariables.settings.pageType === 'cart';
      }
    } catch (e) {}
    return window.location.pathname.indexOf('/cart') !== -1;
  }

  function afterCartChange() {
    if (isCartPage()) {
      window.location.reload();
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  function fail(button, message) {
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.textContent = message;
  }

  function post(url, payload) {
    return fetch(root.replace(/\/$/, '') + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (response) {
      if (!response.ok) throw new Error('cart request failed');
      return response.json();
    });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-itura-gift-add], [data-itura-gift-surprise]');
    if (!button) return;

    event.preventDefault();
    if (button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    var surprise = button.getAttribute('data-itura-gift-surprise');

    if (surprise) {
      post('/cart/update.js', { attributes: { 'Gift Preference': surprise } })
        .then(afterCartChange)
        .catch(function () {
          fail(button, 'Please try again');
        });
      return;
    }

    var variantId = parseInt(button.getAttribute('data-itura-gift-add'), 10);
    var tierLabel = button.getAttribute('data-itura-gift-tier') || 'Tier 1';

    post('/cart/add.js', {
      items: [{ id: variantId, quantity: 1, properties: { '_ITURA Gift': tierLabel + ' pick' } }]
    })
      .then(function () {
        return fetchCart().then(function (cart) {
          return settleGift(cart, false);
        });
      })
      .then(afterCartChange)
      .catch(function () {
        fail(button, 'Out of stock - pick another');
      });
  });

  /**
   * AUTO GIFT (3 Oct 2026, Paul). The moment a cart holds a paid piece and no gift, add ONE random
   * gift from the list in snippets/itura-gift-menu.liquid (served by sections/itura-gift-config.liquid). The customer can
   * remove it to choose their own, or leave the cart without one:
   *   - a cart that has already held a gift is not auto-gifted again while it still holds a paid piece
   *     (remembered per cart token), so removing the gift sticks and the chooser takes over;
   *   - when the last paid piece leaves the cart the gift leaves with it (a gift alone is not free) and
   *     that memory is wiped, so the next piece added gets a gift again;
   *   - the list is empty once the offer has ended (date gate in itura-gift-menu.liquid), so nothing
   *     is added after 31 October.
   *
   * Speed (3 Oct 2026): the gift used to show about 5 seconds after the cart opened. Now the cart that
   * the theme sends with its own cart:change event is used as it is, the gift list is fetched while
   * the piece is still being added and kept for 10 minutes, and the cart is redrawn as soon as the
   * gift is in (the "is it free" check runs straight after). While the gift is on its way <html>
   * carries .itura-gift-adding, and snippets/itura-gift-picker.liquid shows "Adding your free gift..."
   * in place of the chooser.
   */
  var GIFT_KEY = '_ITURA Gift';
  var SEEN_KEY = 'ituraGiftSeenCart';
  var CONFIG_KEY = 'ituraGiftConfig';
  var CONFIG_TTL = 10 * 60 * 1000;
  var ADDING_CLASS = 'itura-gift-adding';
  var reconciling = false;
  var rerun = false;
  var configMemo = null;
  var configRequest = null;

  function store(value) {
    try {
      if (value === null) window.localStorage.removeItem(SEEN_KEY);
      else if (value !== undefined) window.localStorage.setItem(SEEN_KEY, value);
      return window.localStorage.getItem(SEEN_KEY);
    } catch (e) {
      return null;
    }
  }

  function cachedConfig() {
    try {
      if (!configMemo) configMemo = JSON.parse(window.sessionStorage.getItem(CONFIG_KEY) || 'null');
    } catch (e) {}
    if (configMemo && configMemo.config && Date.now() - configMemo.at < CONFIG_TTL) return configMemo.config;
    configMemo = null;
    return null;
  }

  function giftConfig() {
    var cached = cachedConfig();
    if (cached) return Promise.resolve(cached);
    if (configRequest) return configRequest;

    configRequest = fetch(root.replace(/\/$/, '') + '/?sections=itura-gift-config', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (sections) {
        var holder = document.createElement('div');
        holder.innerHTML = sections['itura-gift-config'] || '';
        var config = JSON.parse(holder.querySelector('#itura-gift-config').textContent) || {};
        configMemo = { at: Date.now(), config: config };
        try {
          window.sessionStorage.setItem(CONFIG_KEY, JSON.stringify(configMemo));
        } catch (e) {}
        return config;
      })
      .catch(function () {
        return {};
      })
      .then(function (config) {
        configRequest = null;
        return config;
      });
    return configRequest;
  }

  function isGiftLine(item) {
    return Boolean(item.properties && item.properties[GIFT_KEY]);
  }

  function addRandomGift(ids, label, attempt) {
    if (!ids.length || attempt > 2) return Promise.resolve(false);
    var index = Math.floor(Math.random() * ids.length);
    var properties = {};
    properties[GIFT_KEY] = label;
    return post('/cart/add.js', { items: [{ id: ids[index], quantity: 1, properties: properties }] })
      .then(function () {
        return true;
      })
      .catch(function () {
        var rest = ids.slice();
        rest.splice(index, 1);
        return addRandomGift(rest, label, attempt + 1);
      });
  }

  function fetchCart() {
    return fetch(root.replace(/\/$/, '') + '/cart.js', { headers: { Accept: 'application/json' } }).then(function (response) {
      return response.json();
    });
  }

  // What the gift line is saving the customer, in kobo. Zero means the gift discount did not land.
  function giftSaving(item) {
    return item.original_line_price - item.final_line_price;
  }

  function setAdding(on) {
    document.documentElement.classList.toggle(ADDING_CLASS, on);
  }

  // The drawer swaps its content when it redraws: that is the moment to drop the "adding" state.
  function clearAddingOnRedraw() {
    var drawer = document.querySelector('cart-drawer');
    var observer = null;
    var timer = window.setTimeout(done, 6000);

    function done() {
      setAdding(false);
      if (observer) observer.disconnect();
      window.clearTimeout(timer);
    }

    if (drawer && window.MutationObserver) {
      observer = new MutationObserver(done);
      observer.observe(drawer, { childList: true, subtree: true });
    }
  }

  /**
   * One pass over the cart; resolves true when it changed the cart.
   * Guards (3 Oct 2026): a gift line is only ever left in the cart when it is FREE.
   *   - not free (offer ended, a discount code replaced the offer, variant no longer on the list)
   *     -> the line is taken out, so nobody is charged for a gift they did not ask for;
   *   - quantity above 1 -> back to 1 (one gift per order; the second unit would be charged).
   */
  function settleGift(cart, mayAdd) {
    var gifts = cart.items.filter(isGiftLine);
    var paid = cart.items.filter(function (item) {
      return !isGiftLine(item);
    });

    // No paid piece left: the cart starts afresh, so the next piece added gets a gift again.
    if (!paid.length) store(null);

    if (gifts.length) {
      var updates = {};
      var dirty = false;
      if (!paid.length) {
        gifts.forEach(function (item) {
          updates[item.key] = 0;
        });
        dirty = true;
      } else {
        store(cart.token);
        gifts.forEach(function (item) {
          if (giftSaving(item) <= 0) {
            updates[item.key] = 0;
            dirty = true;
          } else if (item.quantity > 1) {
            updates[item.key] = 1;
            dirty = true;
          }
        });
      }
      if (!dirty) return Promise.resolve(false);
      return post('/cart/update.js', { updates: updates }).then(function () {
        return true;
      });
    }

    if (!mayAdd || !paid.length || store() === cart.token) return Promise.resolve(false);

    setAdding(true);
    return giftConfig()
      .then(function (config) {
        return addRandomGift((config.offer || []).slice(), config.label || 'Independence pick', 0);
      })
      .then(function (added) {
        if (added) store(cart.token);
        else setAdding(false);
        return added;
      });
  }

  // knownCart: the cart the theme sends with cart:change. Without one the cart is fetched.
  function reconcileGift(knownCart) {
    if (reconciling) {
      // The cart changed again while a pass was running: look once more when it finishes.
      rerun = true;
      return;
    }
    reconciling = true;
    (knownCart ? Promise.resolve(knownCart) : fetchCart())
      .then(function (cart) {
        return settleGift(cart, true);
      })
      .then(function (didChange) {
        if (!didChange) return false;
        // Show the change at once, then check it: an auto-added gift that did not come out free is taken back out.
        clearAddingOnRedraw();
        afterCartChange();
        return fetchCart().then(function (cart) {
          return settleGift(cart, false);
        });
      })
      .then(function (didChangeAgain) {
        reconciling = false;
        if (didChangeAgain) afterCartChange();
      })
      .catch(function () {
        reconciling = false;
        setAdding(false);
      })
      .then(function () {
        if (!rerun) return;
        rerun = false;
        reconcileGift();
      });
  }

  document.addEventListener('cart:change', function (event) {
    var cart = event.detail && event.detail.cart;
    reconcileGift(cart && cart.token && Array.isArray(cart.items) ? cart : null);
  });

  // A piece is being added: fetch the gift list now, so it is ready when the cart comes back.
  document.addEventListener(
    'submit',
    function (event) {
      var action = (event.target && event.target.getAttribute && event.target.getAttribute('action')) || '';
      if (action.indexOf('/cart/add') !== -1) giftConfig();
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      reconcileGift();
    });
  } else {
    reconcileGift();
  }

  /**
   * Swym Wishlist "Do you want to save this product for later?" prompt (3 Oct 2026, Paul: turn it off).
   * Swym adds it to the page on every cart removal, the free gift included: it covers the gift chooser,
   * locks page scrolling while it is open, and a real click on it closes the cart drawer. The real
   * switch is in the Swym app (Save for later > pop-up). Until that is off, assets/itura-gift.css keeps
   * the prompt invisible and this closes it through Swym's own close button the moment it is added.
   */
  function closeSaveForLater(attempt) {
    var prompt = document.querySelector('.swym-sfl-popup');
    if (!prompt) return;
    var close = prompt.querySelector('.swym-close-btn');
    if (close) {
      close.click();
    } else if (attempt < 20) {
      window.setTimeout(function () {
        closeSaveForLater(attempt + 1);
      }, 100);
    }
  }

  function watchSaveForLater() {
    if (!window.MutationObserver || !document.body) return;
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1 && added[j].classList.contains('swym-sfl-popup')) {
            closeSaveForLater(0);
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.body) watchSaveForLater();
  else document.addEventListener('DOMContentLoaded', watchSaveForLater);
})();
